import React, { useState, useEffect, useRef } from 'react';
import './chatbot.css';
import { useChatStore } from '../../store/chatStore';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { userAppContext } from '../../context/Context';
import {
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
  ArrowUpIcon,
  CheckIcon,
  TrashIcon,
  ArrowDownTrayIcon as ArrowDownTrayOutline,
  PlusIcon,
  InformationCircleIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  BellIcon,
  BellSlashIcon,
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  ChatBubbleOvalLeftEllipsisIcon,
} from '@heroicons/react/24/outline';
import StopIcon from '@heroicons/react/24/solid/StopIcon';
import RestrictedAccess from '../Modal/RestrictedAccess';
import type { ChatMessageType } from '@/store/types';
import ReasoningBlock from './ReasoningBlock';
import { trackEvent } from '@/utils/analytics';
import { Link } from 'react-router-dom';
import 'highlight.js/styles/github.css';

interface ChatBotProps {
  chatId?: string;
  message?: string;
  question?: string;
  shortQuestion?: string;
  autoSend?: boolean;
  style?: React.CSSProperties;
}

export default function ChatBot({
  chatId = 'default',
  message = '',
  question = '',
  shortQuestion = '',
  autoSend = false,
  style = {},
}: ChatBotProps) {
  const { isAuthenticated, userApiKey, groups, user, authorization } = userAppContext();
  const normalizedGroups = Array.isArray(groups) ? groups : [];
  const trackingGroup =
    normalizedGroups
      .find((group: string) => group.startsWith('ROLE_'))
      ?.replace('ROLE_', '')
      .toLowerCase() || (isAuthenticated ? 'authenticated' : 'anonymous');

  const {
    chatHistory,
    chatSessions,
    aidaNotificationsEnabled,
    composerAlert,
    setChatHistory,
    setAidaNotificationsEnabled,
    clearComposerAlert,
    ensureSession,
    addUserMessage,
    streamBotResponse,
    stopStreaming,
    clearSession,
    deleteSession,
  } = useChatStore();

  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [downloadId, setDownloadId] = useState<string | null>(null);
  const [lastActivityTime, setLastActivityTime] = useState(new Date().getTime());
  const [inputMessage, setInputMessage] = useState('');
  const [hasAutoSent, setHasAutoSent] = useState(false);
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const messagesAreaRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const userName = user?.['custom:name'];
  const [activeChatId, setActiveChatId] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const urlChatId = params.get('chatId');
    return urlChatId ?? chatId;
  });
  const session = chatSessions[activeChatId];
  const messages = session?.messages ?? [];
  const isBotResponding = session?.isStreaming ?? false;
  const isBotTyping = session?.isStreaming ?? false;
  const botStatus = session?.botStatus ?? null;

  const showComposerAlert = composerAlert && composerAlert.chatId === activeChatId ? composerAlert : null;

  const hasConversation = messages.some(m => m.sender === 'user');

  const initialBotMessage: ChatMessageType = {
    id: 'bot',
    sender: 'bot',
    message:
      message !== ''
        ? message
        : 'Hello! How can I help you?\nDo you want to see what kind of questions I can help you answer?',
    timestamp: new Date(),
  };

  const activeChatIdRef = useRef(activeChatId);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('chatId')) {
      const clean = window.location.pathname;
      window.history.replaceState({}, '', clean);
    }
  }, []);

  useEffect(() => {
    activeChatIdRef.current = activeChatId;
  }, [activeChatId]);

  useEffect(() => {
    ensureSession(chatId, initialBotMessage);
    if (chatHistory.length === 0) {
      setChatHistory([initialBotMessage]);
    }
  }, [chatId, ensureSession, setChatHistory]);

  const scrollMessagesToBottom = () => {
    if (messagesAreaRef.current) {
      messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight;
    }
  };

  // Separar los dos handlers que ahora comparten la misma función:

  const handleNewChat = () => {
    trackEvent({
      name: 'aida_new_chat',
      buttonId: `aida_new_chat_${trackingGroup}`,
      label: 'New chat',
      page: 'aida',
      section: 'sidebar',
      feature: 'aida',
      eventCategory: 'aida',
    });
    const newId = `chat-${Date.now()}`;
    ensureSession(newId, initialBotMessage);
    setActiveChatId(newId);
    if (isMobile) setSidebarCollapsed(true);
  };

  const handleEndChat = async (e: React.MouseEvent | null, currentUserApiKey: string) => {
    trackEvent({
      name: 'aida_clear_chat',
      buttonId: `aida_clear_chat_${trackingGroup}`,
      label: 'Clear chat',
      page: 'aida',
      section: 'sidebar',
      feature: 'aida',
      eventCategory: 'aida',
    });
    const currentChatId = activeChatIdRef.current;
    const headers = { Authorization: `Bearer ${authorization}`, 'x-api-key': currentUserApiKey };
    const urlClear = `${process.env.REACT_APP_URL_CHAT_API}clear`;
    try {
      const response = await fetch(urlClear, { method: 'GET', headers, credentials: 'include' });
      if (!response.ok) throw new Error('Network response was not ok');
      stopStreaming(currentChatId, false);
      clearSession(currentChatId, initialBotMessage);
    } catch (error) {
      console.error('There was a problem with the fetch operation:', error);
    }
  };

  const handleLoadSession = (id: string) => {
    setActiveChatId(id);
    if (isMobile) setSidebarCollapsed(true);
  };

  const handleDeleteSession = (id: string) => {
    deleteSession(id);
    if (activeChatId === id) {
      handleNewChat();
    }
  };

  const handleMessagesScroll = () => {
    const el = messagesAreaRef.current;
    if (!el) return;
    const isUserNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    setIsAutoScrollEnabled(isUserNearBottom);
  };

  useEffect(() => {
    if (!isAutoScrollEnabled) return;
    setTimeout(() => scrollMessagesToBottom(), 60);
  }, [messages, isAutoScrollEnabled]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [inputMessage]);

  const resetInactivityTimeout = () => setLastActivityTime(new Date().getTime());

  useEffect(() => {
    const checkInactivity = setInterval(() => {
      if (new Date().getTime() - lastActivityTime >= 3600000) {
        handleEndChat(null, userApiKey);
      }
    }, 600000);
    return () => clearInterval(checkInactivity);
  }, [lastActivityTime, userApiKey]);

  const stopTyping = () => stopStreaming(activeChatIdRef.current, false);

  const handleSendMessage = async () => {
    const messageToSend = (question || inputMessage).trim();
    if (messageToSend === '') return;

    const currentChatId = activeChatIdRef.current;

    if (isBotResponding) {
      stopStreaming(currentChatId, true);
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    clearComposerAlert();

    const currentTime = new Date();
    const userMessage: ChatMessageType = {
      id: `user-${currentTime.getTime()}`,
      sender: 'user',
      message: messageToSend,
      timestamp: currentTime,
    };

    addUserMessage(currentChatId, userMessage);
    setInputMessage('');
    resetInactivityTimeout();
    setIsAutoScrollEnabled(true);
    await streamBotResponse({
      chatId: currentChatId,
      userMessage: userMessage.message,
      userMessageId: userMessage.id,
      user: user,
      authorization: authorization,
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        // allow newline
      } else {
        e.preventDefault();
        trackEvent({
          name: 'aida_send_message',
          buttonId: `aida_send_message_${trackingGroup}`,
          label: 'Send message',
          page: 'aida',
          section: 'chat_input',
          feature: 'aida',
          eventCategory: 'aida',
          additionalParams: { input_method: 'enter_key' },
        });
        handleSendMessage();
      }
    }
  };

  const handleButtonClick = () => {
    trackEvent({
      name: isBotTyping || isBotResponding ? 'aida_stop_response' : 'aida_send_message',
      buttonId: `aida_send_message_${trackingGroup}`,
      label: isBotTyping || isBotResponding ? 'Stop response' : 'Send message',
      page: 'aida',
      section: 'chat_input',
      feature: 'aida',
      eventCategory: 'aida',
      additionalParams: { input_method: isBotTyping || isBotResponding ? 'stop_button' : 'send_button' },
    });
    if (isBotTyping || isBotResponding) {
      stopTyping();
    } else {
      handleSendMessage();
    }
  };

  const handleToggleAidaNotifications = async () => {
    trackEvent({
      name: 'aida_toggle_notifications',
      buttonId: `aida_toggle_notifications_${trackingGroup}`,
      label: aidaNotificationsEnabled ? 'Disable notifications' : 'Enable notifications',
      page: 'aida',
      section: 'sidebar',
      feature: 'aida',
      eventCategory: 'aida',
    });
    if (aidaNotificationsEnabled) {
      setAidaNotificationsEnabled(false);
      return;
    }

    if (typeof window === 'undefined' || typeof Notification === 'undefined') {
      window.alert('Your browser does not support system notifications for AIDA.');
      return;
    }

    if (Notification.permission === 'granted') {
      setAidaNotificationsEnabled(true);
      return;
    }

    const permission = await Notification.requestPermission();
    setAidaNotificationsEnabled(permission === 'granted');
    if (permission !== 'granted') {
      window.alert('AIDA notifications were blocked. Enable browser notifications and try again.');
    }
  };

  const formatTimestamp = (timestamp: Date | string) => {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return new Intl.DateTimeFormat('es-ES', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    }).format(date);
  };

  const normalizeBackendMarkdown = (message: string) => message.replace(/\r\n/g, '\n').trim();

  const markdownComponents = {
    a: ({ href, children, ...props }: { href?: string } & React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
      <a href={href ?? undefined} target="_blank" rel="noopener noreferrer" {...props}>
        {children} <i className="bi bi-box-arrow-up-right"></i>&nbsp;
      </a>
    ),
  };

  const composerMarkdownComponents = {
    p: ({ children }: { children?: React.ReactNode }) => (
      <p className="aida-composer-alert__para">{children}</p>
    ),
    a: ({ href, children }: { href?: string } & React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
      if (href?.startsWith('/')) {
        return (
          <Link to={href} className="aida-composer-alert__link">
            {children}
          </Link>
        );
      }
      return (
        <a
          href={href ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
          className="aida-composer-alert__link"
        >
          {children} <i className="bi bi-box-arrow-up-right" aria-hidden />
        </a>
      );
    },
  };

  const shouldRenderBotMessage = (chatMessage: ChatMessageType) => {
    const hasReasoning = Boolean(chatMessage.reasoningSteps?.length);
    const hasAnswer = Boolean(chatMessage.message?.trim());
    const isInitialMessage = chatMessage.id === initialBotMessage.id;
    return hasReasoning || hasAnswer || isInitialMessage;
  };

  const copyToClipboard = (messageText: string, messageId: string) => {
    navigator.clipboard.writeText(messageText).then(
      () => {
        setCopiedMessageId(messageId);
        setTimeout(() => setCopiedMessageId(null), 1000);
      },
      err => console.error('Error copying:', err),
    );
  };

  const downloadChatAsTxt = () => {
    trackEvent({
      name: 'aida_export_chat',
      buttonId: `aida_export_chat_${trackingGroup}`,
      label: 'Export chat',
      page: 'aida',
      section: 'sidebar',
      feature: 'aida',
      eventCategory: 'aida',
    });
    const textContent = messages
      ?.map(
        msg =>
          `[${formatTimestamp(msg.timestamp)}] ${msg.sender === 'user' ? 'You' : 'AIDA'}: ${msg.message}`,
      )
      ?.join('\n');
    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = 'aida-chat.txt';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    setDownloadId('downloaded');
    setTimeout(() => setDownloadId(null), 2000);
  };

  const isAcademicUser = groups?.includes('ROLE_ACADEMIC');
  const canSend = inputMessage.trim().length > 0;

  useEffect(() => {
    if (composerAlert?.chatId === activeChatId) return;
    if (shortQuestion) setInputMessage(shortQuestion);
    else if (question) setInputMessage(question);
  }, [question, shortQuestion, composerAlert, activeChatId]);

  useEffect(() => {
    if (!composerAlert || composerAlert.chatId !== activeChatId) return;
    setInputMessage(composerAlert.restoreDraft);
    const id = window.setTimeout(() => textareaRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [composerAlert, activeChatId]);

  useEffect(() => {
    if (autoSend && question && !hasAutoSent && isAuthenticated && userApiKey) {
      setHasAutoSent(true);
      setTimeout(() => handleSendMessage(), 500);
    }
  }, [autoSend, question, hasAutoSent, isAuthenticated, userApiKey, chatId]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const onViewportChange = (event: MediaQueryListEvent | MediaQueryList) => {
      const mobile = event.matches;
      setIsMobile(mobile);
      setSidebarCollapsed(mobile);
    };

    onViewportChange(mediaQuery);

    const listener = (event: MediaQueryListEvent) => onViewportChange(event);
    mediaQuery.addEventListener('change', listener);
    return () => mediaQuery.removeEventListener('change', listener);
  }, []);

  const sessionList = Object.entries(chatSessions)
    .map(([id, session]) => {
      const firstUserMsg = session.messages.find(m => m.sender === 'user');
      return {
        id,
        title: firstUserMsg?.message?.slice(0, 60) ?? 'New conversation',
        timestamp: firstUserMsg?.timestamp ?? new Date(0),
        hasMessages: Boolean(firstUserMsg),
      };
    })
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="aida-shell" style={style}>
      {isMobile && !sidebarCollapsed && (
        <button
          className="aida-mobile-backdrop"
          onClick={() => setSidebarCollapsed(true)}
          aria-label="Close menu"
        />
      )}
      {/* ── Sidebar ── */}
      <aside
        className={`aida-sidebar ${sidebarCollapsed ? 'aida-sidebar--collapsed' : ''} ${
          isMobile && !sidebarCollapsed ? 'aida-sidebar--mobile-open' : ''
        }`}
      >
        <div className="aida-sidebar__top">
          <div className="aida-sidebar__logo-wrap">
            {!sidebarCollapsed ? (
              <img src="/static/images/AIDA-logo.svg" alt="AIDA" className="aida-sidebar__logo" />
            ) : (
              <img src="/static/images/eyesAida.svg" alt="AIDA" className="aida-sidebar__logo" />
            )}
          </div>

          <nav className="aida-sidebar__nav">
            <button
              id={`aida_new_chat_${trackingGroup}`}
              className="aida-sidebar__action"
              onClick={handleNewChat}
              title="New chat"
            >
              <PlusIcon className="aida-sidebar__icon" />
              {!sidebarCollapsed && <span>New Chat</span>}
            </button>

            <button
              className="aida-sidebar__action aida-sidebar__action--danger"
              onClick={e => handleEndChat(e, userApiKey as string)}
              title="Clear chat"
              disabled={!hasConversation}
            >
              <TrashIcon className="aida-sidebar__icon" />
              {!sidebarCollapsed && <span>Clear Chat</span>}
            </button>

            <button
              id={`aida_export_chat_${trackingGroup}`}
              className="aida-sidebar__action"
              onClick={downloadChatAsTxt}
              disabled={!hasConversation}
              title="Download chat"
            >
              {downloadId ? (
                <>
                  <CheckIcon className="aida-sidebar__icon" />
                  {!sidebarCollapsed && <span>Downloaded!</span>}
                </>
              ) : (
                <>
                  <ArrowDownTrayOutline className="aida-sidebar__icon" />
                  {!sidebarCollapsed && <span>Export Chat</span>}
                </>
              )}
            </button>

            <button
              id={`aida_toggle_notifications_${trackingGroup}`}
              className={`aida-sidebar__action ${aidaNotificationsEnabled ? 'aida-sidebar__action--active' : ''}`}
              onClick={handleToggleAidaNotifications}
              title={aidaNotificationsEnabled ? 'Disable notifications' : 'Notify me when ready'}
            >
              {aidaNotificationsEnabled ? (
                <BellIcon className="aida-sidebar__icon" />
              ) : (
                <BellSlashIcon className="aida-sidebar__icon" />
              )}
              {!sidebarCollapsed && (
                <span>{aidaNotificationsEnabled ? 'Notifications on' : 'Notify when ready'}</span>
              )}
            </button>

            {sessionList.length > 0 && (
              <div className="aida-sidebar__history">
                {!sidebarCollapsed && (
                  <>
                    <p className="aida-sidebar__privacy-note">
                      <ChatBubbleOvalLeftEllipsisIcon className="h-4 w-4 inline-block mb-1" /> Conversations
                      are stored locally in your browser only — not on our servers. If you leave the website,
                      the conversations will be deleted.
                    </p>
                    <p className="aida-sidebar__history-label">Recent</p>
                  </>
                )}
                <ul className="aida-sidebar__history-list">
                  {sessionList.map(s => (
                    <li
                      key={s.id}
                      className={`aida-sidebar__history-item ${s.id === activeChatId ? 'aida-sidebar__history-item--active' : ''}`}
                    >
                      <button
                        className="aida-sidebar__history-btn"
                        onClick={() => handleLoadSession(s.id)}
                        title={s.title}
                      >
                        <ChatBubbleLeftRightIcon className="aida-sidebar__icon" />
                        {!sidebarCollapsed && <span className="aida-sidebar__history-title">{s.title}</span>}
                      </button>
                      {!sidebarCollapsed && (
                        <button
                          className="aida-sidebar__history-delete"
                          onClick={e => {
                            e.stopPropagation();
                            handleDeleteSession(s.id);
                          }}
                          title="Delete conversation"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </nav>
        </div>
        {/* Collapse toggle */}
        <button
          className="aida-sidebar__collapse-btn ml-2 mb-2"
          onClick={() => setSidebarCollapsed(c => !c)}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? (
            <ChevronDoubleRightIcon className="aida-sidebar__icon" />
          ) : (
            <ChevronDoubleLeftIcon className="aida-sidebar__icon" />
          )}
        </button>

        <div className="aida-sidebar__bottom">
          {!sidebarCollapsed && (
            <button className="aida-sidebar__disclaimer-toggle" onClick={() => setDisclaimerOpen(o => !o)}>
              <InformationCircleIcon className="aida-sidebar__icon" />
              <span>About AIDA</span>
            </button>
          )}

          {disclaimerOpen && !sidebarCollapsed && (
            <div className="aida-sidebar__disclaimer">
              <p>
                AIDA uses OpenAI technology and RAG to provide responses based on DISGENET data. Outputs may
                contain errors and should be independently verified.{' '}
                <a href="/AIDA-disclaimer" target="_blank" rel="noopener noreferrer">
                  Learn more ↗
                </a>
              </p>
            </div>
          )}
        </div>
      </aside>

      {/* ── Main area ── */}
      <main className="aida-main">
        {isMobile && (
          <button
            className="aida-mobile-menu-btn"
            onClick={() => setSidebarCollapsed(c => !c)}
            aria-label={sidebarCollapsed ? 'Open menu' : 'Close menu'}
            title={sidebarCollapsed ? 'Open menu' : 'Close menu'}
          >
            {sidebarCollapsed ? (
              <ChevronDoubleRightIcon className="aida-sidebar__icon" />
            ) : (
              <ChevronDoubleLeftIcon className="aida-sidebar__icon" />
            )}
          </button>
        )}
        <RestrictedAccess
          isRestricted={!isAuthenticated || isAcademicUser}
          restrictedMessage={
            isAcademicUser ? (
              <h6>
                As an academic user, you do not have access to the chat. Please explore other options
                available in{' '}
                <a href="/plans" style={{ color: '#D02591' }}>
                  plans
                </a>
                .
              </h6>
            ) : undefined
          }
        >
          {/* ── Landing state (no conversation yet) ── */}
          {!hasConversation ? (
            <div className="aida-landing">
              <div className="aida-landing__hero">
                <img src="/static/images/AIDA-removebg.png" alt="AIDA" className="aida-landing__logo" />
                <h1 className="aida-landing__title">How can I help you?</h1>
                <p className="aida-landing__subtitle">
                  Do you want to see what kind of questions I can help you answer?
                </p>
              </div>

              <div className="aida-composer-stack aida-composer-stack--landing">
                {showComposerAlert && (
                  <div className="aida-composer-alert" role="alert">
                    <img
                      src="/static/images/eyesAida.svg"
                      alt=""
                      width={24}
                      height={24}
                      className="aida-composer-alert__avatar"
                    />
                    <div className="aida-composer-alert__text">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={composerMarkdownComponents}>
                        {normalizeBackendMarkdown(showComposerAlert.message)}
                      </ReactMarkdown>
                    </div>
                    <button
                      type="button"
                      className="aida-composer-alert__close"
                      onClick={() => clearComposerAlert()}
                      aria-label="Dismiss message"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
                <div className="aida-input-wrap aida-input-wrap--landing">
                  <textarea
                    ref={textareaRef}
                    className="aida-textarea"
                    placeholder="Ask AIDA anything…"
                    value={inputMessage}
                    onChange={e => setInputMessage(e.target.value)}
                    onKeyDown={handleKeyPress}
                    rows={1}
                    disabled={!isAuthenticated}
                  />
                  <button
                    id={`aida_send_message_${trackingGroup}`}
                    className={`aida-send-btn ${canSend || isBotResponding ? 'aida-send-btn--active' : ''}`}
                    onClick={handleButtonClick}
                    aria-label={isBotResponding ? 'Stop' : 'Send'}
                  >
                    {isBotTyping || isBotResponding ? (
                      <StopIcon className="h-5 w-5" />
                    ) : (
                      <ArrowUpIcon className="h-5 w-5" strokeWidth={2.5} />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* ── Active conversation state ── */
            <div className="aida-chat">
              <div className="aida-chat__messages" ref={messagesAreaRef} onScroll={handleMessagesScroll}>
                <div className="aida-chat__inner">
                  {messages.map((msg, index) => (
                    <React.Fragment key={`message-${index}`}>
                      {/* User message */}
                      {msg.sender === 'user' && (
                        <div className="aida-msg aida-msg--user">
                          <div className="aida-msg__meta">
                            <span className="aida-msg__name">{userName}</span>
                            <span className="aida-msg__time">{formatTimestamp(msg.timestamp)}</span>
                          </div>
                          <div className="aida-msg__bubble">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              rehypePlugins={[rehypeHighlight]}
                              components={markdownComponents}
                            >
                              {normalizeBackendMarkdown(msg.message)}
                            </ReactMarkdown>
                          </div>
                        </div>
                      )}

                      {/* Bot message */}
                      {msg.sender === 'bot' && shouldRenderBotMessage(msg) && (
                        <div className="aida-msg aida-msg--bot">
                          <div className="aida-msg__avatar">
                            <img src="/static/images/eyesAida.svg" alt="AIDA" width="28" height="28" />
                          </div>
                          <div className="aida-msg__body">
                            <div className="aida-msg__meta">
                              <span className="aida-msg__name aida-msg__name--bot">AIDA</span>
                              <span className="aida-msg__time">{formatTimestamp(msg.timestamp)}</span>
                              {msg.interrupted && (
                                <span className="aida-msg__interrupted-badge">
                                  Request interrupted by user
                                </span>
                              )}
                            </div>
                            <ReasoningBlock steps={msg.reasoningSteps ?? []} />
                            <div className="aida-msg__text">
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                rehypePlugins={[rehypeHighlight]}
                                components={markdownComponents}
                              >
                                {normalizeBackendMarkdown(msg.message)}
                              </ReactMarkdown>
                            </div>
                            {msg.message !== initialBotMessage.message && msg.isValid && (
                              <button
                                className="aida-copy-btn"
                                onClick={() => copyToClipboard(msg.message, msg.id)}
                              >
                                {copiedMessageId === msg.id ? (
                                  <>
                                    Copied <ClipboardDocumentCheckIcon className="h-4 w-4 inline-block" />
                                  </>
                                ) : (
                                  <>
                                    Copy <ClipboardDocumentIcon className="h-4 w-4 inline-block" />
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  ))}

                  {/* Thinking indicator */}
                  {isBotTyping && (
                    <div className="aida-msg aida-msg--bot">
                      <div className="aida-msg__avatar">
                        <img src="/static/images/eyesAida.svg" alt="AIDA" width="28" height="28" />
                      </div>
                      <div className="aida-msg__body">
                        {botStatus === 'thinking' && (
                          <div className="aida-thinking-loader" aria-label="thinking" role="status">
                            <span className="aida-dot"></span>
                            <span className="aida-dot"></span>
                            <span className="aida-dot"></span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Input bar */}
              <div
                className={`aida-chat__input-area ${isMobile ? 'aida-chat__input-area--mobile' : 'mb-16'}`}
              >
                <div className="aida-composer-stack">
                  {showComposerAlert && (
                    <div className="aida-composer-alert" role="alert">
                      <img
                        src="/static/images/eyesAida.svg"
                        alt=""
                        width={24}
                        height={24}
                        className="aida-composer-alert__avatar"
                      />
                      <div className="aida-composer-alert__text">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={composerMarkdownComponents}>
                          {normalizeBackendMarkdown(showComposerAlert.message)}
                        </ReactMarkdown>
                      </div>
                      <button
                        type="button"
                        className="aida-composer-alert__close"
                        onClick={() => clearComposerAlert()}
                        aria-label="Dismiss message"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  <div className="aida-input-wrap">
                    <textarea
                      ref={textareaRef}
                      className="aida-textarea"
                      placeholder="Ask AIDA anything…"
                      value={inputMessage}
                      onChange={e => setInputMessage(e.target.value)}
                      onKeyDown={handleKeyPress}
                      rows={1}
                    />
                    <button
                      id={`aida_send_message_${trackingGroup}`}
                      className={`aida-send-btn ${canSend || isBotResponding ? 'aida-send-btn--active' : ''}`}
                      onClick={handleButtonClick}
                      aria-label={isBotResponding ? 'Stop' : 'Send'}
                    >
                      {isBotTyping || isBotResponding ? (
                        <StopIcon className="h-5 w-5" />
                      ) : (
                        <ArrowUpIcon className="h-5 w-5" strokeWidth={2.5} />
                      )}
                    </button>
                  </div>
                </div>
                <p className="aida-chat__disclaimer">
                  AIDA can make mistakes. Verify important information.{' '}
                  <a href="/AIDA-disclaimer" target="_blank" rel="noopener noreferrer">
                    Learn more ↗
                  </a>
                </p>
              </div>
            </div>
          )}
        </RestrictedAccess>
      </main>
    </div>
  );
}
